'use client';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { requiredTierFor, type Feature, type Plan } from '@slikerr/shared';
import { Icon } from '@/components/ds/Icon';
import { Badge } from '@/components/ds/Badge';
import { Avatar } from '@/components/ds/Avatar';

// ---------------------------------------------------------------------------
// Nav data
// ---------------------------------------------------------------------------

interface PrimaryItem {
  id: string;
  href: string;
  icon: string;
  labelKey: string;
}

interface GrowthItem extends PrimaryItem {
  /** The entitlements-registry feature this nav item maps to (drives the lock badge). */
  feature: Feature;
}

const PRIMARY_NAV: PrimaryItem[] = [
  { id: 'calendar', href: '/dashboard/calendar', icon: 'calendar-days', labelKey: 'nav.calendar' },
  { id: 'clients', href: '/dashboard/clients', icon: 'users-round', labelKey: 'nav.clients' },
  { id: 'services', href: '/dashboard/services', icon: 'scissors', labelKey: 'nav.services' },
  { id: 'staff', href: '/dashboard/staff', icon: 'contact-round', labelKey: 'nav.staff' },
];

const GROWTH_NAV: GrowthItem[] = [
  {
    id: 'reminders',
    href: '/dashboard/reminders',
    icon: 'bell-ring',
    labelKey: 'nav.reminders',
    feature: 'reminders',
  },
  {
    id: 'reviews',
    href: '/dashboard/reviews',
    icon: 'star',
    labelKey: 'nav.reviews',
    feature: 'reviews',
  },
  {
    id: 'analytics',
    href: '/dashboard/analytics',
    icon: 'chart-column',
    labelKey: 'nav.analytics',
    feature: 'analytics',
  },
  { id: 'ai', href: '/dashboard/ai', icon: 'sparkles', labelKey: 'nav.ai', feature: 'ai' },
];

/** Display label for the required-tier badge (only pro/premium ever show a lock). */
const TIER_BADGE: Partial<Record<Plan, string>> = { pro: 'Pro', premium: 'Premium' };

const SETTINGS_ITEM: PrimaryItem = {
  id: 'settings',
  href: '/dashboard/settings',
  icon: 'settings',
  labelKey: 'nav.settings',
};

// ---------------------------------------------------------------------------
// NavLink sub-component
// ---------------------------------------------------------------------------

function NavLink({
  href,
  icon,
  label,
  active,
  testId,
  badge,
}: {
  href: string;
  icon: string;
  label: string;
  active: boolean;
  testId: string;
  badge?: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderRadius: 'var(--r-md)',
        textDecoration: 'none',
        color: active ? 'var(--accent-deep)' : 'var(--text-secondary)',
        background: active ? 'var(--brand-wash)' : 'transparent',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-body)',
        fontWeight: active ? 600 : 400,
        position: 'relative',
        marginBlockEnd: 2,
        transition: 'background var(--dur-micro)',
      }}
    >
      {/* Active accent bar — inline-end edge */}
      {active && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            insetBlock: 6,
            insetInlineEnd: 0,
            width: 3,
            borderRadius: 'var(--r-full)',
            background: 'var(--brand)',
          }}
        />
      )}
      <Icon name={icon} size={18} color={active ? 'var(--accent-deep)' : 'var(--text-tertiary)'} />
      <span style={{ flex: 1 }}>{label}</span>
      {badge}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// SideNav
// ---------------------------------------------------------------------------

export interface SideNavProps {
  pathname: string;
  businessName: string;
  /**
   * The business's resolved feature entitlements. A growth item shows its
   * tier lock badge only when its feature is NOT in this list. Undefined/empty
   * (still loading) → treated as locked, so a badge never wrongly disappears
   * before entitlements resolve.
   */
  entitlements?: readonly Feature[];
}

export function SideNav({ pathname, businessName, entitlements }: SideNavProps) {
  const { t } = useTranslation();

  const isActive = (href: string) => pathname.startsWith(href);
  // A feature is locked when the plan doesn't grant it. `requiredTierFor` gives
  // the tier whose badge to show (features unlocked at 'basic' never show one).
  const lockBadgeFor = (feature: Feature): string | null => {
    if ((entitlements ?? []).includes(feature)) return null;
    return TIER_BADGE[requiredTierFor(feature)] ?? null;
  };

  return (
    <nav
      aria-label="dashboard navigation"
      style={{
        width: 248,
        flex: '0 0 248px',
        background: 'var(--bg-surface)',
        borderInlineEnd: '1px solid var(--border-subtle)',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 12px',
        boxSizing: 'border-box',
        alignSelf: 'stretch',
      }}
    >
      {/* Business chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          marginBlockEnd: 12,
          borderRadius: 'var(--r-lg)',
          background: 'var(--brand-wash)',
        }}
      >
        <Avatar name={businessName} size={32} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-caption)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {businessName}
          </div>
        </div>
      </div>

      {/* Primary nav */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {PRIMARY_NAV.map((item) => (
          <li key={item.id}>
            <NavLink
              href={item.href}
              icon={item.icon}
              label={t(item.labelKey)}
              active={isActive(item.href)}
              testId={`nav-${item.id}`}
            />
          </li>
        ))}
      </ul>

      {/* Divider */}
      <div
        style={{ height: 1, background: 'var(--border-subtle)', margin: '10px 4px' }}
        aria-hidden="true"
      />

      {/* Growth section label */}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-tertiary)',
          padding: '0 12px 4px',
          fontFamily: 'var(--font-sans)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
        aria-hidden="true"
      >
        {t('nav.growth')}
      </div>

      {/* Growth (locked) nav */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {GROWTH_NAV.map((item) => {
          const lockTier = lockBadgeFor(item.feature);
          return (
            <li key={item.id}>
              <NavLink
                href={item.href}
                icon={item.icon}
                label={t(item.labelKey)}
                active={isActive(item.href)}
                testId={`nav-${item.id}`}
                badge={lockTier ? <Badge tone="premium">{lockTier}</Badge> : undefined}
              />
            </li>
          );
        })}
      </ul>

      {/* Spacer */}
      <div style={{ flex: 1 }} aria-hidden="true" />

      {/* Settings — pinned at bottom */}
      <NavLink
        href={SETTINGS_ITEM.href}
        icon={SETTINGS_ITEM.icon}
        label={t(SETTINGS_ITEM.labelKey)}
        active={isActive(SETTINGS_ITEM.href)}
        testId={`nav-${SETTINGS_ITEM.id}`}
      />
    </nav>
  );
}
